import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";

/**
 * What an UNCOMMISSIONED or DEACTIVATED user sees.
 *
 * The reason `/auth/me` answers 200 for them at all. Without this screen
 * the only honest options are a 403 page or an endless bounce back to the
 * IdP, and neither tells somebody the true state of affairs: they signed
 * in correctly and are waiting on a person.
 */
export const Route = createFileRoute("/_authed/pending-activation")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    // An activated user landing here from a stale bookmark goes to the
    // dashboard rather than reading that they are still waiting.
    if (me.status === "active") throw redirect({ to: "/" });
  },
  component: PendingActivation,
});

function PendingActivation() {
  const { t } = useTranslation();
  const { me } = useSession();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [checking, setChecking] = useState(false);
  const deactivated = me.status === "deactivated";

  /**
   * **Both invalidations, and in this order.**
   *
   * This used to invalidate the query alone, which refetched `/auth/me`
   * and then left the person exactly where they were. The redirect to the
   * dashboard lives in this route's `beforeLoad`, and a *query*
   * invalidation does not re-run a route — so an account that had just
   * been activated pressed "Check again", nothing visible happened, and
   * the screen went on saying they were waiting. The copy above promises
   * "this page will work as soon as they do", so that was the button
   * failing at its only job. Found by `e2e/journey-activation.spec.ts`,
   * which presses it rather than navigating around it (DEE-41).
   *
   * The query first: `beforeLoad` reads the session through
   * `ensureQueryData`, which serves whatever is cached, so re-running the
   * route against a stale entry would decide on the old status.
   */
  const check = async (): Promise<void> => {
    setChecking(true);
    try {
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      await router.invalidate();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mx-auto max-w-prose">
      <PageHeader
        title={deactivated ? t("auth.deactivated.title") : t("auth.pending.title")}
        description={deactivated ? t("auth.deactivated.body") : t("auth.pending.body")}
      />
      {!deactivated && (
        <Button
          variant="secondary"
          disabled={checking}
          onClick={() => void check()}
          tooltip={t("tooltips.checkAgain")}
        >
          {t("auth.pending.action")}
        </Button>
      )}
    </div>
  );
}
